/**
 * PermitCloseoutPanel — Phase 1F
 * ─────────────────────────────────────────────────────────────────────
 * Structured closeout-readiness surface for the Permit Detail drawer.
 *
 * Visibility:
 *   - inspection lifecycle complete
 *   - OR permitCloseout already exists
 *   - OR permit stage is Approved / Closed
 *
 * Pure presentation + form glue. All mutations route through the
 * `permitCloseout.ts` helpers via the supplied callbacks.
 */
import { useMemo, useState } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import type { Job } from "@/data/types";
import {
  resolveCloseoutReadiness,
  closeoutMetadataSchema,
  type CloseoutMetadataInput,
  type PermitCloseoutItemStatus,
} from "@/data/permitting/permitCloseout";
import { resolveInspectionReadiness } from "@/data/permitting/inspectionLifecycle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardCheck, CheckCircle2, MinusCircle, Circle, FileCheck,
  User as UserIcon, Calendar, Hash, Pencil,
} from "lucide-react";
import { formatShortDate } from "@/lib/dateFormat";

interface Props {
  job: Job;
  onOpenCloseout: (jobId: string) => void;
  onSetMetadata: (jobId: string, input: CloseoutMetadataInput) => void;
  onCycleItem: (jobId: string, itemId: string, by: string) => void;
  /** Stamp source for `completedBy` (typically `useCurrentRep().repName`). */
  actor: string;
}

// ── Metadata sub-form ──────────────────────────────────────────────

function MetadataForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial: { owner?: string; municipalityClosureReference?: string; closureConfirmedDate?: string; notes?: string };
  onSubmit: (input: CloseoutMetadataInput) => void;
  onCancel: () => void;
}) {
  const [owner, setOwner] = useState(initial.owner ?? "");
  const [ref, setRef] = useState(initial.municipalityClosureReference ?? "");
  const [closureDate, setClosureDate] = useState(
    initial.closureConfirmedDate ? initial.closureConfirmedDate.slice(0, 10) : "",
  );
  const [notes, setNotes] = useState(initial.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const isoClosure = closureDate ? new Date(`${closureDate}T12:00:00Z`).toISOString() : undefined;
    const parsed = closeoutMetadataSchema.safeParse({
      owner: owner || undefined,
      municipalityClosureReference: ref || undefined,
      closureConfirmedDate: isoClosure,
      notes: notes || undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid");
      return;
    }
    onSubmit(parsed.data);
  };

  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px]">Closeout owner</Label>
          <Input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Coordinator owning closure"
            className="h-8 text-xs mt-1"
          />
        </div>
        <div>
          <Label className="text-[11px]">Municipality closure reference</Label>
          <Input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="e.g. CO-2026-04812"
            className="h-8 text-xs mt-1"
          />
        </div>
        <div>
          <Label className="text-[11px]">Closure confirmed date</Label>
          <Input
            type="date"
            value={closureDate}
            onChange={(e) => setClosureDate(e.target.value)}
            className="h-8 text-xs mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="text-xs mt-1 resize-none"
          />
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs">Cancel</Button>
        <PermissionGate permission="jobs_approve">
          <Button size="sm" onClick={submit} className="h-7 text-xs">Save</Button>
        </PermissionGate>
      </div>
    </div>
  );
}

// ── Item row ────────────────────────────────────────────────────────

function ItemRow({
  label,
  status,
  completedAt,
  completedBy,
  onCycle,
}: {
  label: string;
  status: PermitCloseoutItemStatus;
  completedAt?: string;
  completedBy?: string;
  onCycle: () => void;
}) {
  const tone =
    status === "complete" ? "bg-success/[0.05] border-success/30" :
    status === "na" ? "bg-muted/40 border-border" :
    "bg-card border-border";
  const Icon = status === "complete" ? CheckCircle2 : status === "na" ? MinusCircle : Circle;
  const iconCls =
    status === "complete" ? "text-success" :
    status === "na" ? "text-muted-foreground" :
    "text-muted-foreground/60";

  return (
    <PermissionGate permission="jobs_approve">
      <button
        type="button"
        onClick={onCycle}
        className={`w-full text-left rounded-md border p-2.5 ${tone} hover:border-primary/40 transition-colors`}
        title="Click to cycle: pending → complete → N/A → pending"
      >
      <div className="flex items-start gap-2.5">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${iconCls}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium ${status === "na" ? "text-muted-foreground line-through" : "text-foreground"}`}>
              {label}
            </span>
            {status === "complete" && (
              <Badge variant="outline" className="bg-success/15 text-success border-success/30 text-[10px]">Complete</Badge>
            )}
            {status === "na" && (
              <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-[10px]">N/A</Badge>
            )}
          </div>
          {status === "complete" && (completedAt || completedBy) && (
            <div className="flex flex-wrap items-center gap-x-3 text-[10px] text-muted-foreground mt-0.5">
              {completedBy && <span className="flex items-center gap-1"><UserIcon className="h-2.5 w-2.5" />{completedBy}</span>}
              {completedAt && <span className="flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />{formatShortDate(completedAt)}</span>}
            </div>
          )}
        </div>
      </div>
    </button>
    </PermissionGate>
  );
}

// ── Main panel ─────────────────────────────────────────────────────

export function PermitCloseoutPanel({
  job,
  onOpenCloseout,
  onSetMetadata,
  onCycleItem,
  actor,
}: Props) {
  const closeout = job.permitDetail?.permitCloseout ?? null;
  const inspection = useMemo(
    () => resolveInspectionReadiness(job.permitDetail?.inspectionLifecycle ?? null),
    [job.permitDetail?.inspectionLifecycle],
  );
  const readiness = useMemo(() => resolveCloseoutReadiness(closeout), [closeout]);

  const stage = job.permitDetail?.detailedStage;
  const stageEligible = stage === "Approved" || stage === "Closed";
  const inspectionsComplete = inspection.status === "complete";
  const showPanel = inspectionsComplete || !!closeout || stageEligible;
  const [editingMeta, setEditingMeta] = useState(false);

  if (!showPanel || !job.permitDetail) return null;

  // Closeout not opened yet.
  if (!closeout) {
    const empty = inspectionsComplete
      ? "Inspections complete — permit closeout not started"
      : "Permit closeout not started";
    return (
      <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <FileCheck className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold text-foreground">Permit Closeout Readiness</h4>
          <Badge variant="outline" className="bg-muted text-muted-foreground border-border">Not Started</Badge>
        </div>
        <p className="text-xs text-muted-foreground">{empty}</p>
        <PermissionGate permission="jobs_approve">
          <Button size="sm" onClick={() => onOpenCloseout(job.id)} className="h-7 text-xs gap-1">
            <ClipboardCheck className="h-3 w-3" /> Open Permit Closeout
          </Button>
        </PermissionGate>
      </div>
    );
  }

  const headerBadgeCls =
    readiness.tone === "green" ? "bg-success/15 text-success border-success/30" :
    readiness.tone === "amber" ? "bg-warning/15 text-warning border-warning/30" :
    "bg-muted text-muted-foreground border-border";

  return (
    <div className="rounded-lg border p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <FileCheck className="h-4 w-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold text-foreground">Permit Closeout Readiness</h4>
        <Badge variant="outline" className={headerBadgeCls}>{readiness.badgeLabel}</Badge>
      </div>

      {/* Metadata strip */}
      {!editingMeta && (
        <div className="rounded-md border bg-muted/20 p-3 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
            <span className="flex items-center gap-1">
              <UserIcon className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Owner</span>
              <span className="font-medium text-foreground">{closeout.owner || "—"}</span>
            </span>
            <span className="flex items-center gap-1">
              <Hash className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Closure ref</span>
              <span className="font-medium text-foreground tabular-nums">
                {closeout.municipalityClosureReference || "—"}
              </span>
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Confirmed</span>
              <span className="font-medium text-foreground tabular-nums">
                {closeout.closureConfirmedDate ? formatShortDate(closeout.closureConfirmedDate) : "—"}
              </span>
            </span>
            <PermissionGate permission="jobs_approve">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-[11px] gap-1 ml-auto"
                onClick={() => setEditingMeta(true)}
              >
                <Pencil className="h-3 w-3" /> Edit
              </Button>
            </PermissionGate>
          </div>
          {closeout.notes && (
            <p className="text-[11px] text-muted-foreground italic pt-1 border-t">{closeout.notes}</p>
          )}
        </div>
      )}

      {editingMeta && (
        <MetadataForm
          initial={{
            owner: closeout.owner,
            municipalityClosureReference: closeout.municipalityClosureReference,
            closureConfirmedDate: closeout.closureConfirmedDate,
            notes: closeout.notes,
          }}
          onSubmit={(input) => { onSetMetadata(job.id, input); setEditingMeta(false); }}
          onCancel={() => setEditingMeta(false)}
        />
      )}

      {/* Items */}
      <div className="space-y-1.5">
        {closeout.items.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No closeout checklist items</p>
        ) : (
          closeout.items.map((item) => (
            <ItemRow
              key={item.id}
              label={item.label}
              status={item.status}
              completedAt={item.completedAt}
              completedBy={item.completedBy}
              onCycle={() => onCycleItem(job.id, item.id, actor)}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t">
        <span className="text-xs tabular-nums text-muted-foreground">
          {readiness.progressLabel}
          {readiness.naCount > 0 && (
            <span className="ml-2">· {readiness.naCount} N/A</span>
          )}
        </span>
        {readiness.state === "ready" && (
          <Badge variant="outline" className="bg-success/15 text-success border-success/30">
            Ready to Close Permit
          </Badge>
        )}
      </div>
    </div>
  );
}
